import Vue from 'vue';

import {
  setHeapIdentity,
  setHeapGlobalEventProperties
} from '../services/heapTracking';
import {
  setSentryUserIdentity,
  setSentryTagForGlobalEventProperties
} from '../services/sentryTracking';
import {
  getUserSub,
  isUserRemotelyControlled,
  isUserSessionSetup
} from '../services/account/userSession';
import { getAccessibleUnitsList } from '../services/account/user';
import viewTypes from './view-types';

/**
 * Check if app is bing used while in CI environments
 * @returns boolean
 */
export function isPipeline(): boolean {
  const isClientSide = typeof window !== 'undefined';
  const inPipeline =
    !isClientSide ||
    window?.Cypress ||
    window?.isChromatic ||
    window?.isStorybook;

  return inPipeline;
}
/**
 * Get list of profile or user properties
 * Those properties should not change within the same session and should very rarely change between logins
 * They relate directly to a user and giving us insight on their user archetype and what they have access to
 *
 * Those properties require the user to be logged in and new values would erase previous values
 * They should be set only once after login
 * @returns Record
 */
export function getProfileProperties(): Record<
  string,
  string | number | boolean
> {
  const userId = getUserSub();
  if (!userId) {
    return;
  }

  // Send org name or id as fallback if getting name failed
  const whiteboxesAccessList = getAccessibleUnitsList();

  const userData = {
    skUserId: userId,
    isStaff: isUserRemotelyControlled(),
    whiteboxesAccessList: whiteboxesAccessList?.join(',') || undefined,
    userLocale: Vue.config.lang
  };
  return userData;
}

/**
 * Get list of global event properties
 * Those properties are unrelated to a user and can change while the user is using the app
 * They give us insight on usage and what actions are users performing
 *
 * Most should not require the user to be logged in but might only be available once logged in
 * They can be updated as many time as needed so we get the latest value
 * @returns Record
 */
export function getGlobalEventProperties(): Record<
  string,
  string | number | boolean
> {
  const isLoggedIn = isUserSessionSetup();
  const unitId = viewTypes.activeView?.data?.unitId;
  const panelId = viewTypes.activeView?.data?.panel;

  return {
    isLoggedIn,
    unitId,
    unitPanelId: panelId
  };
}

/**
 * Set user identity for all tracking tools
 *
 * @returns void
 */
export function setUserIdentity(): void {
  setHeapIdentity();
  setSentryUserIdentity();
}

/**
 * Set user global properties for all tracking tools
 *
 * @returns void
 */
export function setGlobalEventProperties(): void {
  setHeapGlobalEventProperties();
  setSentryTagForGlobalEventProperties();
}
